/**
 * Recover from stale lazy-chunk failures after a deploy.
 *
 * Vite fingerprints every code-split chunk (`EditorPage-<hash>.js`). A deploy
 * publishes new hashes and PURGES the old ones from the asset bucket. A browser
 * tab still running the previous `index-<hash>.js` references the old chunk
 * names, so the next lazy-route navigation fetches a chunk that no longer
 * exists — the bucket's SPA fallback returns index.html (200, text/html), and
 * `nosniff` correctly refuses to execute HTML as a module. React surfaces this
 * as "Failed to fetch dynamically imported module" and the ErrorBoundary shows
 * an error screen.
 *
 * Vite emits a cancelable `vite:preloadError` event when a dynamic import fails.
 * We reload once to pull the current bundle, which references the current chunk
 * hashes — the tab self-heals instead of dead-ending on the error screen.
 *
 * Loop guard: if we already reloaded in the last RELOAD_DEBOUNCE_MS, the failure
 * is NOT a stale chunk (the fresh bundle is failing too) — let the error
 * propagate to the ErrorBoundary rather than reload-looping on a broken build.
 * The timestamp is per-tab (sessionStorage), so a later deploy recovers again.
 */
const RELOAD_KEY = 'vite:preloadError:lastReload';
export const RELOAD_DEBOUNCE_MS = 10_000;

export function installChunkReloadRecovery(win: Window = window): void {
  win.addEventListener('vite:preloadError', (event) => {
    let last = 0;
    try {
      last = Number(win.sessionStorage.getItem(RELOAD_KEY) ?? 0);
    } catch {
      // sessionStorage can throw (private mode / disabled) — treat as no prior reload.
    }
    const now = Date.now();
    if (now - last < RELOAD_DEBOUNCE_MS) {
      // Reloaded moments ago and still failing → genuinely broken build, not a
      // stale chunk. Let the ErrorBoundary handle it (no preventDefault).
      return;
    }
    // We're navigating away — suppress the default throw to avoid a flash of the
    // error boundary before the reload lands.
    event.preventDefault();
    try {
      win.sessionStorage.setItem(RELOAD_KEY, String(now));
    } catch {
      // If we can't record the timestamp we lose loop protection, but a single
      // reload is still better than dead-ending on the error screen.
    }
    win.location.reload();
  });
}
